import { useState, useCallback, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { initTelegramBotAuth, checkTelegramBotAuth, type AuthSession } from '@/utils/auth-pb';
import { pb, isUser } from '@/utils/pocketbase';

interface TelegramBotLoginButtonProps {
  onAuth: (session: AuthSession) => void;
  onError: (error: string) => void;
  label?: string;
  size?: 'large' | 'medium';
}

function TelegramIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.71L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z" />
    </svg>
  );
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 75; // 75 * 2s = 150s

export function TelegramBotLoginButton({
  onAuth,
  onError,
  label,
  size = 'large',
}: TelegramBotLoginButtonProps) {
  const { t } = useTranslation('auth');
  const resolvedLabel = label ?? t('telegram.label');
  const [isWaiting, setIsWaiting] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptsRef = useRef(0);

  const cleanup = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    attemptsRef.current = 0;
    setIsWaiting(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleClick = useCallback(async () => {
    if (isWaiting) return;

    try {
      setIsWaiting(true);
      attemptsRef.current = 0;

      const { code, botUrl } = await initTelegramBotAuth();

      // Open bot in new tab/window
      window.open(botUrl, '_blank');

      // Start polling
      intervalRef.current = setInterval(async () => {
        attemptsRef.current++;

        if (attemptsRef.current > MAX_POLL_ATTEMPTS) {
          cleanup();
          onError(t('telegram.timeout'));
          return;
        }

        try {
          const result = await checkTelegramBotAuth(code);

          if (result.status === 'completed' && result.token && result.record) {
            cleanup();

            if (!isUser(result.record)) {
              onError(t('telegram.serverError'));
              return;
            }

            pb.authStore.save(result.token, result.record as any);
            onAuth({ token: result.token, user: result.record });
          }
        } catch {
          // Ignore transient errors, keep polling
        }
      }, POLL_INTERVAL_MS);
    } catch (err) {
      cleanup();
      onError((err as { message?: string }).message || t('telegram.initError'));
    }
  }, [isWaiting, onAuth, onError, cleanup]);

  const isLarge = size === 'large';

  if (isWaiting) {
    return (
      <div className="w-full">
        <div
          className={`flex items-center justify-center gap-[10px] w-full rounded-[12px] bg-[#54a9eb] ${
            isLarge ? 'h-[52px]' : 'h-[44px]'
          }`}
        >
          <Loader2 className="h-5 w-5 animate-spin text-white" />
          <span
            className={`font-segoe font-semibold text-white ${
              isLarge ? 'text-[14px]' : 'text-[13px]'
            }`}
          >
            {t('telegram.waiting')}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            cleanup();
          }}
          className="mt-2 w-full text-center text-[13px] text-[#565858] hover:text-[#2197f1] transition-colors"
        >
          {t('telegram.cancel')}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`flex items-center justify-center gap-[10px] w-full rounded-[12px] bg-[#54a9eb] cursor-pointer transition-colors hover:bg-[#4a9ae0] ${
        isLarge ? 'h-[52px]' : 'h-[44px]'
      }`}
    >
      <TelegramIcon className={`text-white ${isLarge ? 'h-[22px] w-[22px]' : 'h-[18px] w-[18px]'}`} />
      <span
        className={`font-segoe font-semibold text-white ${
          isLarge ? 'text-[16px]' : 'text-[14px]'
        }`}
      >
        {resolvedLabel}
      </span>
    </button>
  );
}
